import React, { useEffect, useState } from 'react';
import { Header } from '../../components/layout/Header';
import { reportsApi } from '../../lib/api';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { formatDate, formatDateRange, formatNumber } from '../../lib/utils';
import { FileText, Download, Eye, Calendar, TrendingUp, FileDown, Presentation } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { exportToPDF, exportToPPTX, getEffectiveMetrics } from '../../lib/exportUtils';

function GoalBar({ label, actual, goal }) {
    const pct = goal > 0 ? Math.min((actual / goal) * 100, 100) : 0;
    const isOver = actual >= goal;
    return (
        <div className="flex items-center gap-3">
            <span className="text-xs text-content-muted w-20 shrink-0">{label}</span>
            <div className="flex-1 h-2 bg-surface-card rounded-full overflow-hidden">
                <div
                    className={`h-full rounded-full transition-all ${isOver ? 'bg-status-success' : 'bg-brand-red'}`}
                    style={{ width: `${pct}%` }}
                />
            </div>
            <span className={`text-xs font-bold shrink-0 ${isOver ? 'text-status-success' : 'text-brand-red'}`}>
                {actual}/{goal} ({pct.toFixed(0)}%)
            </span>
        </div>
    );
}

export default function ReportsPage({ reportType = 'weekly' }) {
    const [loading, setLoading] = useState(true);
    const [reports, setReports] = useState([]);
    const [exporting, setExporting] = useState(null);

    useEffect(() => {
        loadReports();
    }, [reportType]); // eslint-disable-line react-hooks/exhaustive-deps

    const loadReports = async () => {
        try {
            const response = await reportsApi.getAll(reportType);
            setReports(response.data);
        } catch (error) {
            toast.error('Failed to load reports');
        } finally {
            setLoading(false);
        }
    };

    const handleExportPDF = async (report) => {
        setExporting(`pdf-${report.id}`);
        try {
            exportToPDF(report, reportType);
            toast.success('PDF exported successfully');
        } catch (error) {
            console.error('PDF export error:', error);
            toast.error('Failed to export PDF');
        } finally {
            setExporting(null);
        }
    };

    const handleExportPPT = async (report) => {
        setExporting(`ppt-${report.id}`);
        try {
            await exportToPPTX(report, reportType);
            toast.success('PowerPoint exported successfully');
        } catch (error) {
            console.error('PPT export error:', error);
            toast.error('Failed to export PowerPoint');
        } finally {
            setExporting(null);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-brand-red/30 border-t-brand-red rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen" data-testid="reports-page">
            <Header 
                title={`${reportType.charAt(0).toUpperCase() + reportType.slice(1)} Reports`}
                subtitle="View and export your ABM performance reports"
            />

            <div className="p-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                    {reports.map((report) => (
                        <div 
                            key={report.id}
                            className="card-base card-hover p-6"
                            data-testid={`report-card-${report.id}`}
                        >
                            <div className="flex items-start justify-between mb-4">
                                <div className="p-3 rounded-lg bg-brand-red/10">
                                    <FileText className="w-6 h-6 text-brand-red" />
                                </div>
                                <Badge className="badge badge-info uppercase">
                                    {report.report_type}
                                </Badge>
                            </div>

                            <div className="flex items-center gap-2 text-sm text-content-secondary mb-4">
                                <Calendar className="w-4 h-4" />
                                {formatDateRange(report.period_start, report.period_end)}
                            </div>

                            <div className="grid grid-cols-3 gap-4 mb-4">
                                <div>
                                    <p className="text-2xl font-heading font-bold text-white">
                                        {(report.linkedin?.reachouts_sent || 0) + (report.email?.emails_sent || 0)}
                                    </p>
                                    <p className="text-xs text-content-muted">Reachouts</p>
                                </div>
                                <div>
                                    <p className="text-2xl font-heading font-bold text-channel-linkedin">
                                        {report.linkedin?.connection_accepted || 0}
                                    </p>
                                    <p className="text-xs text-content-muted">Connects</p>
                                </div>
                                <div>
                                    <p className="text-2xl font-heading font-bold text-status-success">
                                        {(report.linkedin?.positive_responses || 0) + (report.email?.positive_replies || 0)}
                                    </p>
                                    <p className="text-xs text-content-muted">Positive Responses</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-3 gap-4 mb-6">
                                <div>
                                    <p className="text-2xl font-heading font-bold text-channel-email">
                                        {report.email?.opened || 0}
                                    </p>
                                    <p className="text-xs text-content-muted">Emails Opened</p>
                                </div>
                                <div>
                                    <p className="text-2xl font-heading font-bold text-brand-red">
                                        {(report.linkedin?.mqls || 0) + (report.email?.mqls || 0)}
                                    </p>
                                    <p className="text-xs text-content-muted">MQLs</p>
                                </div>
                                <div>
                                    <p className="text-2xl font-heading font-bold text-status-success">
                                        {(report.linkedin?.qualified_leads || 0) + (report.email?.qualified_leads || 0)}
                                    </p>
                                    <p className="text-xs text-content-muted">SQLs</p>
                                </div>
                            </div>

                            {/* Goal vs Actual */}
                            {report.goals && (report.goals.target_mqls > 0 || report.goals.target_qualified_leads > 0 || report.goals.total_reachouts > 0) && (
                                <div className="mb-4 p-3 rounded-lg bg-surface-app border border-line-default" data-testid={`goal-vs-actual-${report.id}`}>
                                    <h4 className="text-xs font-bold text-content-muted uppercase tracking-wider mb-3">
                                        Goal vs Actual
                                    </h4>
                                    <div className="space-y-2">
                                        {report.goals.target_mqls > 0 && (
                                            <GoalBar label="MQLs" actual={getEffectiveMetrics(report).mqls} goal={report.goals.target_mqls} />
                                        )}
                                        {report.goals.target_qualified_leads > 0 && (
                                            <GoalBar label="SQLs" actual={getEffectiveMetrics(report).qualified_leads} goal={report.goals.target_qualified_leads} />
                                        )}
                                        {report.goals.total_reachouts > 0 && (
                                            <GoalBar label="Reachouts" actual={getEffectiveMetrics(report).total_reachouts} goal={report.goals.total_reachouts} />
                                        )}
                                        {report.goals.responses > 0 && (
                                            <GoalBar label="Responses" actual={getEffectiveMetrics(report).responses} goal={report.goals.responses} />
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Insights & Observations */}
                            {report.insights && report.insights.length > 0 && (
                                <div className="mb-4">
                                    <h4 className="text-xs font-bold text-content-muted uppercase tracking-wider mb-2">
                                        Insights & Observations
                                    </h4>
                                    <ul className="space-y-1">
                                        {report.insights.slice(0, 3).map((insight, idx) => (
                                            <li key={idx} className="text-sm text-content-secondary flex items-start gap-2">
                                                <span className="text-brand-red mt-1">•</span>
                                                <span className="line-clamp-2">{insight}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {/* Recommendations */}
                            {report.recommendations && report.recommendations.length > 0 && (
                                <div className="mb-4">
                                    <h4 className="text-xs font-bold text-content-muted uppercase tracking-wider mb-2">
                                        Recommendations
                                    </h4>
                                    <ul className="space-y-1">
                                        {report.recommendations.slice(0, 3).map((rec, idx) => (
                                            <li key={idx} className="text-sm text-content-secondary flex items-start gap-2">
                                                <span className="text-status-success mt-1">•</span>
                                                <span className="line-clamp-2">{rec}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            <div className="flex gap-2" data-testid={`report-export-actions-${report.id}`}>
                                <Button 
                                    variant="ghost" 
                                    size="sm"
                                    className="btn-ghost flex-1 gap-2"
                                    onClick={() => handleExportPDF(report)}
                                    disabled={!!exporting}
                                    data-testid={`export-pdf-${report.id}`}
                                >
                                    {exporting === `pdf-${report.id}` ? (
                                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                    ) : (
                                        <FileDown className="w-4 h-4" />
                                    )}
                                    PDF
                                </Button>
                                <Button 
                                    variant="ghost" 
                                    size="sm"
                                    className="btn-ghost flex-1 gap-2"
                                    onClick={() => handleExportPPT(report)}
                                    disabled={!!exporting}
                                    data-testid={`export-ppt-${report.id}`}
                                >
                                    {exporting === `ppt-${report.id}` ? (
                                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                    ) : (
                                        <Presentation className="w-4 h-4" />
                                    )}
                                    PPTX
                                </Button>
                                <Link to={`/reports/${report.id}`}>
                                    <Button 
                                        variant="ghost" 
                                        size="sm"
                                        className="btn-ghost gap-2"
                                        data-testid={`view-report-${report.id}`}
                                    >
                                        <Eye className="w-4 h-4" />
                                    </Button>
                                </Link>
                            </div>
                        </div>
                    ))}
                </div>

                {reports.length === 0 && (
                    <div className="text-center py-16">
                        <FileText className="w-16 h-16 text-content-muted mx-auto mb-4" />
                        <h3 className="text-lg font-heading font-bold text-white mb-2">
                            No Reports Yet
                        </h3>
                        <p className="text-content-secondary">
                            Reports will appear here once your campaigns generate data.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}
